import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, Trophy, Plus, Settings, User, UsersRound, AlertCircle } from 'lucide-react'
import type { League } from './types'

interface LeaguesTabProps {
  leagues: League[]
  leaguesLoading: boolean
  leaguesError: string | null
  onCreateLeague: () => void
  onManageLeague: (leagueId: string) => void
}

export function LeaguesTab({
  leagues,
  leaguesLoading,
  leaguesError,
  onCreateLeague,
  onManageLeague,
}: LeaguesTabProps) {

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Trophy className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Leagues</CardTitle>
                <CardDescription>
                  {leagues.length} {leagues.length === 1 ? 'league' : 'leagues'} in this group
                </CardDescription>
              </div>
            </div>
            <Button onClick={onCreateLeague}>
              <Plus className="h-4 w-4 mr-2" />
              Create League
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {leaguesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : leaguesError ? (
            <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <span className="text-sm text-destructive">{leaguesError}</span>
            </div>
          ) : leagues.length === 0 ? (
            <div className="text-center py-12 rounded-xl border-2 border-dashed">
              <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-semibold mb-2">No leagues yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first league to start tracking rankings and matches.
              </p>
              <Button onClick={onCreateLeague}>
                <Plus className="h-4 w-4 mr-2" />
                Create League
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {leagues.map((league) => (
                <button
                  key={league.leagueId}
                  onClick={() => onManageLeague(league.leagueId)}
                  className="w-full flex items-center justify-between rounded-xl border bg-muted/30 p-4 hover:bg-muted/50 transition-colors text-left"
                >
                  <div className="flex items-center gap-4">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                      league.status === 'active' 
                        ? 'bg-green-500/10' 
                        : 'bg-gray-500/10'
                    }`}>
                      <Trophy className={`h-5 w-5 ${
                        league.status === 'active' 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-gray-500'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{league.name}</span>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                          league.format === 'singles'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                        }`}>
                          {league.format === 'singles' ? (
                            <>
                              <User className="h-3 w-3" />
                              Singles
                            </>
                          ) : (
                            <>
                              <UsersRound className="h-3 w-3" />
                              Doubles
                            </>
                          )}
                        </span>
                        {league.status !== 'active' && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                            Archived
                          </span>
                        )}
                      </div>
                      {league.description && (
                        <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">
                          {league.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <Settings className="h-5 w-5 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
